/**
 * Browser error reporting -> Discord.
 *
 * Every uncaught error, unhandled promise rejection, failed route/chunk load,
 * failed callable and `console.error` in the app is sent to the
 * `reportClientError` function (functions/src/monitoring/clientErrors.ts).
 * That function writes it to Cloud Logging, and the same log-sink pipeline
 * that alerts on backend errors posts it to Discord, grouped with repeats.
 *
 * The Discord webhook URL never reaches the browser; the relay holds it.
 *
 * On by default in production builds only (not dev, not demo mode, not
 * tests). Override with VITE_ERROR_REPORTING=true / false.
 *
 * Reporting must never break the app or feed itself, so:
 *  - every step is wrapped and failures are swallowed silently;
 *  - each error object is reported at most once (a failed callable that a
 *    page then console.errors is one report, not two);
 *  - repeats of one error are throttled, and a page load sends at most
 *    MAX_REPORTS_PER_WINDOW reports per window, so a render loop that throws
 *    every frame costs a handful of requests, not thousands.
 */

import type { App } from 'vue'
import type { Router } from 'vue-router'
import { isChunkLoadError, reloadForStaleChunk, reloadedForStaleChunkRecently } from './staleChunkReload'

export type ClientErrorKind = 'vue' | 'window' | 'unhandledrejection' | 'router' | 'chunk' | 'api' | 'console'

export interface ClientErrorContext {
  callable?: { name: string; code?: string; details?: unknown }
  /** Callable payload. Redacted and truncated before sending. */
  request?: unknown
  component?: string
  lifecycle?: string
  /** Where the error was raised, e.g. a script URL with line:column. */
  source?: string
  /** Text logged alongside the error (console.error's other arguments); prefixed to the message. */
  note?: string
}

export interface ClientErrorReport {
  kind: ClientErrorKind
  message: string
  name?: string
  stack?: string
  page: string
  userAgent: string
  build: string
  occurredAt: string
  tagId?: string
  component?: string
  lifecycle?: string
  source?: string
  request?: string
  callable?: { name: string; code?: string; details?: unknown }
  idToken?: string
}

export const DEDUPE_WINDOW_MS = 30_000
export const RATE_WINDOW_MS = 5 * 60_000
export const MAX_REPORTS_PER_WINDOW = 15

const MAX_MESSAGE = 1500
const MAX_STACK = 5000
const MAX_REQUEST = 2000
const REDACTED = '[redacted]'
const SENSITIVE_KEY = /pass|token|secret|otp|mfa|credential|code/i

/** Browser noise that says nothing about this app. */
const IGNORED_MESSAGES = [
  /^ResizeObserver loop/,
  /^Script error\.?$/,
  // Firebase's own abort when a tab navigates mid-request.
  /^AbortError/,
  // ApexCharts rejects its in-flight async render when the target container has
  // already been unmounted - navigating away from a chart page, or a `v-if`
  // swapping the chart out mid-render. By definition nobody was going to see
  // that chart, so there is nothing to act on. The dashboard's own teardown
  // race (fetchSummaryForDate) is fixed at the source; this covers the
  // navigate-away case, which no page-level fix can avoid.
  /^Element not found$/,
]

interface NormalizedError {
  message: string
  name?: string
  stack?: string
}

export function normalizeError(error: unknown): NormalizedError {
  if (error instanceof Error) {
    return { message: error.message || error.name, name: error.name, stack: error.stack }
  }
  if (typeof error === 'string') return { message: error }
  if (error && typeof error === 'object') {
    const obj = error as { message?: unknown; name?: unknown; stack?: unknown }
    if (typeof obj.message === 'string') {
      return {
        message: obj.message,
        name: typeof obj.name === 'string' ? obj.name : undefined,
        stack: typeof obj.stack === 'string' ? obj.stack : undefined,
      }
    }
    return { message: safeStringify(error, MAX_MESSAGE) }
  }
  return { message: String(error) }
}

function safeStringify(value: unknown, max: number): string {
  try {
    const text = JSON.stringify(value) ?? String(value)
    return text.length > max ? `${text.slice(0, max)}… [truncated]` : text
  } catch {
    return String(value)
  }
}

/** Replaces values under sensitive-looking keys (passwords, tokens, MFA codes) at any depth. */
export function redact(value: unknown, depth = 0): unknown {
  if (depth > 6 || value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.slice(0, 50).map((v) => redact(v, depth + 1))
  const out: Record<string, unknown> = {}
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    out[key] = SENSITIVE_KEY.test(key) ? REDACTED : redact(v, depth + 1)
  }
  return out
}

function clip(text: string | undefined, max: number): string | undefined {
  if (!text) return undefined
  return text.length > max ? `${text.slice(0, max)}… [truncated]` : text
}

function isIgnored(error: NormalizedError): boolean {
  if (IGNORED_MESSAGES.some((re) => re.test(error.message))) return true
  // Errors thrown by browser extensions injected into the page.
  return /(chrome|moz|safari(-web)?)-extension:\/\//.test(error.stack ?? '')
}

export interface ErrorReporterOptions {
  enabled: boolean
  send: (report: ClientErrorReport) => Promise<void> | void
  getIdToken?: () => Promise<string | null>
  now?: () => number
  build?: string
}

export interface ErrorReporter {
  report(kind: ClientErrorKind, error: unknown, context?: ClientErrorContext): void
  /** True when this exact error object has already been reported. */
  wasReported(error: unknown): boolean
}

export function createErrorReporter(options: ErrorReporterOptions): ErrorReporter {
  const now = options.now ?? (() => Date.now())
  const reportedObjects = new WeakSet<object>()
  const lastSentByFingerprint = new Map<string, number>()
  let sentTimes: number[] = []

  function currentTagId(): string | undefined {
    try {
      return localStorage.getItem('selectedTagId') ?? undefined
    } catch {
      return undefined
    }
  }

  async function deliver(report: ClientErrorReport): Promise<void> {
    try {
      if (options.getIdToken) {
        const token = await Promise.race([
          options.getIdToken().catch(() => null),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000)),
        ])
        if (token) report.idToken = token
      }
      await options.send(report)
    } catch {
      // Nowhere left to report a reporting failure.
    }
  }

  function report(kind: ClientErrorKind, error: unknown, context: ClientErrorContext = {}): void {
    try {
      if (!options.enabled) return
      if (error && typeof error === 'object') {
        if (reportedObjects.has(error)) return
        reportedObjects.add(error)
      }

      const normalized = normalizeError(error)
      if (isIgnored(normalized)) return

      const t = now()
      const message = context.note ? `${context.note} ${normalized.message}` : normalized.message
      const fingerprint = `${kind}|${context.callable?.name ?? ''}|${message.slice(0, 200)}`
      const last = lastSentByFingerprint.get(fingerprint)
      if (last !== undefined && t - last < DEDUPE_WINDOW_MS) return
      sentTimes = sentTimes.filter((sentAt) => t - sentAt < RATE_WINDOW_MS)
      if (sentTimes.length >= MAX_REPORTS_PER_WINDOW) return
      lastSentByFingerprint.set(fingerprint, t)
      sentTimes.push(t)

      const payload: ClientErrorReport = {
        kind,
        message: clip(message, MAX_MESSAGE) ?? '(no message)',
        name: normalized.name,
        stack: clip(normalized.stack, MAX_STACK),
        page: typeof location !== 'undefined' ? `${location.pathname}${location.search}` : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        build: options.build ?? 'unknown',
        occurredAt: new Date(t).toISOString(),
        tagId: currentTagId(),
        component: context.component,
        lifecycle: context.lifecycle,
        source: context.source,
      }
      if (context.callable) payload.callable = context.callable
      if (context.request !== undefined) payload.request = safeStringify(redact(context.request), MAX_REQUEST)

      void deliver(payload)
    } catch {
      // Never let reporting throw into the code path that hit the error.
    }
  }

  return {
    report,
    wasReported: (error) => !!error && typeof error === 'object' && reportedObjects.has(error),
  }
}

// ---------------------------------------------------------------------------
// App wiring
// ---------------------------------------------------------------------------

export function isErrorReportingEnabled(env: Record<string, unknown> = import.meta.env): boolean {
  if (env.VITE_ERROR_REPORTING === 'true') return true
  if (env.VITE_ERROR_REPORTING === 'false') return false
  return env.PROD === true && env.VITE_DEMO_MODE !== 'true' && env.MODE !== 'test'
}

export function reportEndpoint(env: Record<string, unknown> = import.meta.env): string {
  if (typeof env.VITE_ERROR_REPORT_URL === 'string' && env.VITE_ERROR_REPORT_URL) return env.VITE_ERROR_REPORT_URL
  return `https://europe-west2-${env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/reportClientError`
}

async function sendToRelay(report: ClientErrorReport): Promise<void> {
  const body = JSON.stringify(report)
  // text/plain + no-cors is a CORS "simple request": no preflight, so the
  // report still gets through when something is wrong with the functions'
  // CORS setup - itself one of the failures worth reporting. keepalive lets it
  // survive the page unloading, up to the browser's 64KB keepalive cap.
  await fetch(reportEndpoint(), {
    method: 'POST',
    mode: 'no-cors',
    keepalive: body.length < 60_000,
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body,
  })
}

async function currentIdToken(): Promise<string | null> {
  const { auth } = await import('@/config/firebase')
  return (await auth.currentUser?.getIdToken()) ?? null
}

export const errorReporter = createErrorReporter({
  enabled: isErrorReportingEnabled(),
  send: sendToRelay,
  getIdToken: currentIdToken,
  build: import.meta.env.VITE_APP_BUILD ?? 'unknown',
})

export function reportError(kind: ClientErrorKind, error: unknown, context?: ClientErrorContext): void {
  errorReporter.report(kind, error, context)
}

let installed = false

/**
 * Hooks every global error surface. Call before Sentry.init: Sentry wraps an
 * existing app.config.errorHandler rather than replacing it, so both see
 * component errors.
 */
export function installErrorReporting(app: App, router: Router, reporter: ErrorReporter = errorReporter): void {
  if (installed) return
  installed = true

  const previousHandler = app.config.errorHandler
  app.config.errorHandler = (err, instance, info) => {
    reporter.report('vue', err, {
      component: instance?.$options?.name ?? (instance?.$options as { __name?: string } | undefined)?.__name,
      lifecycle: info,
    })
    if (previousHandler) previousHandler(err, instance, info)
    // With a handler set, Vue no longer logs the error itself.
    else console.error(err)
  }

  router.onError((err, to) => {
    // A stale chunk is recovered by reloading onto the new build, landing
    // on the page the user was navigating to. Reporting a chunk failure is
    // left to the vite:preloadError handler below, which only does it once
    // a reload has already failed to fix it.
    if (isChunkLoadError(err)) {
      reloadForStaleChunk(to?.fullPath)
      return
    }
    reporter.report('router', err)
  })

  window.addEventListener('error', (event) => {
    if (!(event instanceof ErrorEvent)) return
    reporter.report('window', event.error ?? event.message, {
      source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    reporter.report('unhandledrejection', event.reason)
  })

  // Vite fires this when a lazily-loaded chunk fails to load - almost always
  // a tab left open across a deploy requesting a chunk that no longer exists.
  // That is recovered by reloading (see staleChunkReload.ts), so it is only
  // worth reporting if a reload already happened and it STILL failed - e.g. a
  // deploy that is genuinely missing assets.
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadedForStaleChunkRecently()) {
      reporter.report('chunk', (event as Event & { payload?: unknown }).payload ?? 'Failed to preload a chunk')
      return
    }
    // Deferred so that, for a route navigation, router.onError reloads first
    // onto the intended page; this then finds the guard set and does nothing.
    // For a chunk loaded outside the router, this is the reload.
    setTimeout(() => reloadForStaleChunk(), 0)
  })

  const originalConsoleError = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    originalConsoleError(...args)
    try {
      const errorArg = args.find((a) => a instanceof Error)
      if (errorArg && reporter.wasReported(errorArg)) return
      const text = args
        .filter((a) => a !== errorArg)
        .map((a) => (typeof a === 'string' ? a : safeStringify(a, 500)))
        .join(' ')
      if (errorArg instanceof Error) {
        reporter.report('console', errorArg, { note: text || undefined })
      } else if (text) {
        reporter.report('console', text)
      }
    } catch {
      // ignore
    }
  }
}

/** Test-only: allow installErrorReporting to run again. */
export function __resetInstalledForTests(): void {
  installed = false
}
